/**
 * EditableCell — Select-then-edit inline cell editor for DataTable.
 *
 * Interaction model (spreadsheet-style, "split" click model):
 * - Desktop single click SELECTS the cell and stops propagation, so an
 *   editable cell never triggers row navigation. Touch taps are ignored so
 *   the row's tap-to-open-slideout still works.
 * - Selection renders as an Attio-style "popped field" (CellFieldOverlay): a
 *   floating rounded container anchored over the cell that reveals the full
 *   untruncated value. Edit mode reuses the SAME container, hosting the
 *   editor, which may grow beyond the column width.
 * - Double click, Enter, or F2 on a selected cell enters edit mode.
 * - Non-editable cells are not wrapped by this component at all, so clicking
 *   them still fires the row's onRowClick (open record) as before.
 *
 * While editing:
 * - Tab commits the current value and moves focus to the next editable cell in the same row.
 * - Enter commits the current value and moves focus to the same column in the next row.
 * - Escape discards the current draft and closes the editor, returning focus
 *   to the cell. If the save fails, the editor stays open showing the error;
 *   Escape again discards the draft and closes.
 * - Blur (clicking away) commits and saves.
 * - Validation errors are shown in a tooltip; the cell stays in edit mode.
 * - Screen reader announcements are emitted via an aria-live region on focus entry.
 * - When editing, the cell's own tabindex is removed so only the input is reachable.
 *
 * The `onSave` callback is expected to call `useUpdateCrmProperty.mutateAsync` (or equivalent)
 * and throw on error so the cell can stay open and show the validation message.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { AssociatedCrmObjectInput } from '@/lib/crm/components/associations/AssociatedCrmObjectInput';
import { EntityBadgeByIds } from '@/lib/crm/components/badges/EntityBadgeByIds';
import { ExpandableMultiValueLabel } from '@/lib/crm/components/properties/labels/ExpandableMultiValueLabel';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import { PropertyInput } from '@/lib/crm/components/properties/PropertyInput';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { getChromeOverrides } from '@/lib/crm/inline-edit/chromeOverrides';
import {
  classifyEditorKind,
  isPhoneCardEditor as isPhoneCardEditorKind,
} from '@/lib/crm/inline-edit/editorKinds';
import { isPopoverOpenWithin } from '@/lib/crm/inline-edit/isPopoverOpenWithin';
import { useInlineEdit } from '@/lib/crm/inline-edit/useInlineEdit';
import { valuesEqual } from '@/lib/crm/utils/valuesEqual';
import { cx } from '@/utils/cx';

import { useCellSubscription } from '../hooks/useCellSubscription';
import { CellFieldOverlay } from './cell-field-overlay';

// Markdown cards get a wider floor than the generic card minimum (see
// editCardMinWidthPx below): prose at the generic 340px wraps awkwardly.
const MARKDOWN_CARD_MIN_WIDTH_PX = 480;

// Double-click window used to distinguish a deliberate single click on an
// association badge (open the record slideout) from the first click of a
// double-click (enter the cell's inline editor).
const BADGE_NAV_DELAY_MS = 250;

/**
 * Read-state content for an editable cell: association target badges, or the
 * canonical property value label. Used both in-grid (truncated) and inside
 * the popped selection field (full reveal).
 *
 * Association badges are clickable-through to the target's record slideout
 * via `onBadgeClick` — navigation is delayed by the caller so a double-click
 * still enters the cell's inline editor instead of firing two navigations.
 * When `onBadgeClick` is omitted, badges render non-interactive (the split
 * click model, where a single click on the rest of the cell SELECTS it).
 */
function CellReadValue({
  value,
  propertyType,
  association,
  truncate = false,
  onBadgeClick,
}: {
  value: unknown;
  propertyType?: PropertyTypeSchema;
  association?: EditableCellProps['association'];
  truncate?: boolean;
  onBadgeClick?: (id: string) => void;
}) {
  // Per-value schema for the expanded multi-value list below. Hoisted above
  // the early returns (hooks-before-returns) and memoised so the label rows
  // keep a stable propertyType reference across re-renders.
  const singleValueType = React.useMemo(
    () =>
      propertyType?.is_multi
        ? { ...propertyType, is_multi: false }
        : propertyType,
    [propertyType]
  );
  if (association) {
    const ids = Array.isArray(value) ? (value as string[]) : [];
    if (ids.length === 0) {
      return <span className='text-fg-quaternary'>—</span>;
    }
    return (
      <EntityBadgeByIds
        entityIds={ids}
        orgId={association.orgId}
        variant='xs'
        maxVisible={truncate ? 1 : undefined}
        wrap={false}
        interactive={!!onBadgeClick}
        onClick={onBadgeClick}
      />
    );
  }
  if (!propertyType) {
    return null;
  }
  // Full-reveal (popped selection field) on a multi-value property: expand
  // to ALL values, one per line — the in-grid "first value +N" compression
  // exists only because the grid row is one line tall. The popped field is
  // exactly the surface that answers "what's behind the +N".
  if (
    !truncate &&
    propertyType.is_multi &&
    singleValueType &&
    Array.isArray(value) &&
    value.length > 1
  ) {
    return (
      <div className='flex flex-col items-start gap-1 py-2'>
        {value.map((v, index) => (
          <PropertyValueLabel
            // Multi-values have no stable identity beyond position; rows are
            // never reordered inside the read-only overlay.
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            value={v}
            propertyType={singleValueType}
            fontSize='sm'
            truncate={false}
          />
        ))}
      </div>
    );
  }
  return (
    <ExpandableMultiValueLabel
      value={value}
      propertyType={propertyType}
      fontSize='sm'
      truncate={truncate}
      mode='popover'
    />
  );
}

export interface EditableCellProps {
  /** Stable row entity ID */
  entityId: string;
  /** Property name being edited (matches column key) */
  propertyName: string;
  /**
   * Schema for the property — drives which PropertyInput variant is
   * rendered. Omitted for association cells (see `association`).
   */
  propertyType?: PropertyTypeSchema;
  /**
   * Association cells: the association schema plus the org used to
   * search/resolve target entities. The cell's value is then the complete
   * list of target entity ids (string[]), edited with the canonical
   * AssociatedCrmObjectInput picker. Exactly one of propertyType or
   * association must be provided.
   */
  association?: {
    associationType: AssociationType;
    orgId: number;
    /**
     * Whether a single-cardinality association's value may be cleared.
     * Defaults to true; set false for required associations (e.g. a
     * pipeline/lifecycle stage an entity must always have).
     */
    allowClear?: boolean;
  };
  /** Current persisted value shown when not editing */
  value: unknown;
  /** Human-readable column label used in screen-reader announcements */
  columnLabel: string;
  /** Whether this specific cell is currently in edit mode */
  isEditing: boolean;
  /** Called when the user initiates editing (click / F2 / Enter on focused cell) */
  onStartEdit: () => void;
  /** Called after a successful save so the parent can clear the active edit cell */
  onCommitEdit: () => void;
  /**
   * Called when the edit is abandoned (Escape while a failed save's error is
   * showing) so the parent can clear the active edit cell.
   */
  onCancelEdit: () => void;
  /**
   * Async save function. Should persist the new value (e.g. via useUpdateCrmProperty).
   * Throwing causes the cell to stay in edit mode and display the error message.
   */
  onSave: (value: unknown) => Promise<void>;
  /** Navigate to the next editable cell in the same row (Tab) */
  onTabNext?: () => void;
  /** Navigate to the same column in the next row (Enter) */
  onEnterNext?: () => void;
  /** Disable editing entirely (read-only mode) */
  disabled?: boolean;
}

/**
 * Inline editable cell that wraps PropertyInput.
 *
 * The component is intentionally *uncontrolled* about the pending edit value:
 * it holds a local draft until blur/Tab/Enter, then calls `onSave`.
 */
const EditableCellInner: React.FC<EditableCellProps> = ({
  entityId,
  propertyName,
  propertyType,
  association,
  value,
  columnLabel,
  isEditing,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onSave,
  onTabNext,
  onEnterNext,
  disabled = false,
}) => {
  // Subscribe to store for real-time updates from other users via WebSocket.
  // storeValue is debounced (50ms) and RAF-batched, keeping the cell's value
  // live as remote edits land.
  const { storeValue } = useCellSubscription(entityId, propertyName);

  const openSlideout = useOpenSlideoutOptional();
  // Pending badge → slideout navigation. A single deliberate click opens
  // the slideout after BADGE_NAV_DELAY_MS; a double-click (edit) cancels it.
  const badgeNavTimerRef = useRef<number | null>(null);

  // Effective value: store data takes priority (may be newer from WS events),
  // fall back to prop value when entity is not yet in the store.
  const effectiveValue = storeValue !== undefined ? storeValue : value;

  // Editor kind — drives which chrome overrides apply, the overlay layout,
  // and the commit semantics (pickers commit on pick; cards commit on
  // blur/Tab with Enter adding values).
  const isAssociationEditor = !!association;
  const descriptor = classifyEditorKind({
    propertyType,
    association: association
      ? { associationType: association.associationType }
      : undefined,
  });
  const isPickerEditor = descriptor.kind === 'picker';
  // Date pickers (date + datetime) get the compact trigger and an
  // auto-opened calendar — entering edit mode IS the intent to pick.
  // Plain dates additionally commit on pick (choosing the day IS the edit,
  // and the calendar closes on select). Datetime does NOT: date + time is a
  // two-step pick inside one popover, and committing after the first step
  // would save a half-finished value — it commits on blur/outside click
  // (see useInlineEdit's commitTrigger: 'date' is 'pick', 'datetime' is
  // 'blur-only').
  const isDateEditor =
    descriptor.kind === 'date' || descriptor.kind === 'datetime';
  // Markdown edits as a flush card: the rich text editor owns its internal
  // sections (typing area + bottom toolbar with its own divider), so the
  // overlay renders flush for the toolbar's border to span edge to edge.
  const isMarkdownEditor = descriptor.kind === 'markdown';
  // Single-cardinality associations behave like pickers: choosing the target
  // IS the edit, so the pick commits immediately.
  const isSingleAssociation = descriptor.kind === 'single-association';
  // Simple multi-value properties edit as a CARD: the full value-list editor
  // (sortable rows + add input) fills the popped field — the Attio phone/
  // multi-value treatment. Composite multis (subproperties) keep their own
  // editor.
  const isCardEditor = descriptor.kind === 'multi-value-card';
  // The multi-value PHONE card's rows are width-capped fields
  // (PhoneInputBase), so the card hugs that content instead of stretching to
  // the overlay's generic 340px card floor — which read as a comically wide
  // field over a narrow Phone column. Distinct from the single-value phone
  // kind ('phone'), which uses the 'row' layout where no floor applies.
  const isPhoneCardEditor = isPhoneCardEditorKind(descriptor, propertyType);
  // Single associations use the picker's embedded variant, which owns its
  // internal sections (chip, search, list) — the overlay card renders flush
  // so its dividers span edge to edge. Markdown is flush for the same
  // reason: the editor's typing area + toolbar carry their own dividers.
  let editOverlayLayout: 'row' | 'card' | 'card-flush' = 'row';
  if (isSingleAssociation || isMarkdownEditor) {
    editOverlayLayout = 'card-flush';
  } else if (isCardEditor || isAssociationEditor) {
    editOverlayLayout = 'card';
  }
  // Card width floor. Phone cards hug their width-capped rows (see
  // isPhoneCardEditor above). Markdown cards get a wider floor than the
  // generic 340px card minimum — prose at 340px wraps awkwardly; the
  // overlay's own max width still caps growth.
  let editCardMinWidthPx: number | undefined;
  if (isPhoneCardEditor) {
    editCardMinWidthPx = 0;
  } else if (isMarkdownEditor) {
    editCardMinWidthPx = MARKDOWN_CARD_MIN_WIDTH_PX;
  }

  // Screen-reader announcement text. Kept local (not useInlineEdit's own
  // `announcement`) because this cell needs two distinct messages the hook
  // doesn't produce: an entry announcement naming the column/value, and a
  // "{columnLabel} saved" success message rather than the hook's generic
  // "Saved".
  const [announcement, setAnnouncement] = useState('');

  // Ref forwarded into PropertyInput's underlying <input>
  const inputRef = useRef<HTMLInputElement>(null);
  // Ref to the outer cell div so we can return focus on Escape
  const cellRef = useRef<HTMLDivElement>(null);
  // Ref to the floating field overlay — the editor lives in a portal, so all
  // focus-containment checks (blur commit, open-popover detection) run
  // against this element instead of the in-grid cell.
  const overlayRef = useRef<HTMLDivElement>(null);

  // The parent gridcell — the anchor the popped field overlays, and the
  // element whose focus (roving tabindex OR click-focus on this inner div,
  // via bubbling focusin) constitutes "selected". `undefined` = not yet
  // resolved (pre-mount); `null` = resolved, no gridcell ancestor (the cell
  // is rendered outside a DataTable, e.g. unit tests) — the edit overlay
  // then anchors to the cell div itself and selection tracking is off.
  const [gridcellEl, setGridcellEl] = useState<HTMLElement | null | undefined>(
    undefined
  );
  const [isSelected, setIsSelected] = useState(false);

  useEffect(() => {
    // Reaching for the parent is deliberate: roving keyboard focus lands on
    // the gridcell itself, which this component cannot observe through React
    // props without threading focus state through the whole table (a render
    // per arrow-key move). data-editable-gridcell is stamped by DataTable on
    // cells that have an edit config.
    const el =
      cellRef.current?.closest<HTMLElement>('[data-editable-gridcell]') ?? null;
    setGridcellEl(el);
    if (!el) {
      return undefined;
    }
    const handleFocusIn = () => setIsSelected(true);
    const handleFocusOut = (e: FocusEvent) => {
      if (!el.contains(e.relatedTarget as Node)) {
        setIsSelected(false);
      }
    };
    el.addEventListener('focusin', handleFocusIn);
    el.addEventListener('focusout', handleFocusOut);
    return () => {
      el.removeEventListener('focusin', handleFocusIn);
      el.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // useInlineEdit owns the draft value, the save/no-op/error state machine,
  // the re-entrancy and Escape-cancel guards, the blur-commit RAF deferral,
  // and the document-level pointerdown-outside listener. `triggerRef` is
  // deliberately left unattached: this grid cell manages focus-after-close
  // itself per interaction path (Escape/abandon refocus the cell explicitly
  // below; Tab/Enter hand off to onTabNext/onEnterNext; blur/outside-click
  // leave focus where the user put it) — those are the non-portable grid
  // concerns the hook doesn't know about, so its own built-in refocus must
  // stay a no-op here rather than fight with them.
  const focusReturnRef = useRef<HTMLElement | null>(null);
  // Which parent callback the next hook-driven close should invoke. Defaults
  // to the commit path; the Escape "abandon" branch flips this to 'cancel'
  // immediately before calling the hook's cancelEdit().
  const pendingCloseKindRef = useRef<'commit' | 'cancel'>('commit');

  const handleHookSave = useCallback(
    async (v: unknown) => {
      await onSave(v);
      setAnnouncement(`${columnLabel} saved`);
    },
    [onSave, columnLabel]
  );

  const handleHookClose = useCallback(() => {
    if (pendingCloseKindRef.current === 'cancel') {
      pendingCloseKindRef.current = 'commit';
      onCancelEdit();
    } else {
      onCommitEdit();
    }
  }, [onCancelEdit, onCommitEdit]);

  const {
    draft: draftValue,
    setDraft: handleDraftChange,
    errorMessage,
    startEdit,
    cancelEdit,
    commit,
    handleBlur,
  } = useInlineEdit({
    descriptor,
    initialValue: effectiveValue,
    onSave: handleHookSave,
    onClose: handleHookClose,
    containerRef: overlayRef,
    triggerRef: focusReturnRef,
  });

  // Auto-focus the editor when entering edit mode
  useEffect(() => {
    if (isEditing) {
      // Small delay so the DOM has rendered PropertyInput
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          return;
        }
        // Enumerated pickers and date/datetime editors render a button
        // trigger instead of an input. Focus it so the auto-opened options
        // are keyboard-navigable and the popover's focus scope is anchored
        // inside the editor (an unanchored defaultOpen popover can close
        // itself on mount). The popover portals to document.body, so the
        // first button inside the overlay is always the trigger.
        if (isPickerEditor || isDateEditor) {
          overlayRef.current?.querySelector('button')?.focus();
        }
      }, 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isEditing, isPickerEditor, isDateEditor]);

  // Build the display string for screen-reader announcements
  const formatDisplayValue = () => {
    if (
      effectiveValue === null ||
      effectiveValue === undefined ||
      effectiveValue === ''
    ) {
      return 'empty';
    }
    if (typeof effectiveValue === 'object') {
      return JSON.stringify(effectiveValue);
    }
    return String(effectiveValue);
  };
  const displayValue = formatDisplayValue();

  const cancelPendingBadgeNav = useCallback(() => {
    if (badgeNavTimerRef.current !== null) {
      clearTimeout(badgeNavTimerRef.current);
      badgeNavTimerRef.current = null;
    }
  }, []);

  const handleStartEdit = useCallback(() => {
    // Entering edit mode always cancels any pending badge-navigation timer —
    // handleStartEdit is the single cancel choke point for edit-mode entry.
    cancelPendingBadgeNav();
    if (disabled) {
      return;
    }
    // Touch devices should open a slideout — only activate on non-touch pointer
    startEdit();
    setAnnouncement(`Edit mode for ${columnLabel}: ${displayValue}`);
    onStartEdit();
  }, [
    cancelPendingBadgeNav,
    disabled,
    startEdit,
    columnLabel,
    displayValue,
    onStartEdit,
  ]);

  // Track whether the last pointer-down was a touch so badges can be
  // rendered non-interactive for a touch tap (onClick doesn't carry
  // pointerType). This MUST be state, not a ref: a pointerdown has to
  // trigger a re-render that lands before the following click is
  // dispatched, so the onBadgeClick prop passed to CellReadValue below
  // already reflects the current pointer type by the time useActivatable's
  // handleClick runs (and unconditionally stopPropagation()s) on that click.
  // A ref mutation alone triggers no re-render, so a real tap gesture would
  // still see the interactive badge from the last actual render.
  const [lastPointerType, setLastPointerType] = useState('');

  const handleBadgeClick = useCallback(
    (id: string) => {
      cancelPendingBadgeNav();
      badgeNavTimerRef.current = window.setTimeout(() => {
        badgeNavTimerRef.current = null;
        openSlideout?.(id);
      }, BADGE_NAV_DELAY_MS);
    },
    [openSlideout, cancelPendingBadgeNav]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      setLastPointerType(e.pointerType);
      // Touch taps must keep bubbling to the row's tap-to-open-slideout —
      // never schedule cell-driven navigation for them. Only the PRIMARY
      // (left) button navigates: the badge's click-based nav fired for the
      // primary button only (native click), but pointerdown fires for every
      // button, so gate on e.button so a right/middle click (context menu,
      // open-in-new-tab) never silently opens the slideout.
      // pointerType: 'pen' is intentionally NOT gated here — a pen/stylus
      // press is a precise single-contact primary-button interaction,
      // equivalent to a mouse click for navigation purposes, so it falls
      // through and behaves like mouse.
      if (e.pointerType === 'touch' || e.button !== 0) {
        return;
      }
      // Schedule badge -> slideout navigation at POINTERDOWN time rather than
      // relying on the badge's click event. The focus-driven selection
      // overlay (CellFieldOverlay, portaled to document.body) mounts between
      // mousedown and mouseup of the first click and physically covers the
      // badge, so the badge's own click lands on the overlay (document.body)
      // and never fires. pointerdown fires BEFORE the overlay pops, so keying
      // off it makes the first click both expand the cell AND open the
      // record. A following dblclick still cancels via handleStartEdit /
      // handleDoubleClick, and unmount cancels via cancelPendingBadgeNav.
      if (!(e.target instanceof Element)) {
        return;
      }
      const badgeEl = e.target.closest<HTMLElement>(
        '[data-component="entity-badge"][data-entity-id]'
      );
      const badgeId = badgeEl?.getAttribute('data-entity-id');
      if (badgeId) {
        handleBadgeClick(badgeId);
      }
    },
    [handleBadgeClick]
  );

  // Touch taps never reach handleBadgeClick: badges render non-interactive
  // for a touch-originated pointer (passed as onBadgeClick below), so the
  // row's own tap-to-open-slideout still works instead.
  const badgeClickHandler =
    lastPointerType === 'touch' ? undefined : handleBadgeClick;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Ignore touch-originated taps — touch devices should open a slideout.
      if (lastPointerType === 'touch') {
        return;
      }
      if (disabled || isEditing) {
        return;
      }
      // SELECT the cell: focus it (renders the selection ring) and stop
      // propagation so the row click handler (onRowClick) doesn't open the
      // record. Editing starts on double click / Enter / F2 — not on a
      // single click — so a stray click never drops the user into an input.
      e.stopPropagation();
      cellRef.current?.focus();
    },
    [disabled, isEditing, lastPointerType]
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (lastPointerType === 'touch') {
        return;
      }
      // Redundant with the cancel inside handleStartEdit, but this must run
      // before the disabled/isEditing early return below so a double-click
      // on a disabled/already-editing cell still cancels pending navigation
      // even though handleStartEdit is never reached in that case.
      cancelPendingBadgeNav();
      if (disabled || isEditing) {
        return;
      }
      e.stopPropagation();
      handleStartEdit();
    },
    [
      cancelPendingBadgeNav,
      disabled,
      isEditing,
      handleStartEdit,
      lastPointerType,
    ]
  );

  const handleKeyDownCell = useCallback(
    (e: React.KeyboardEvent) => {
      if (isEditing) {
        return;
      }
      // F2 or Enter activates edit mode when the cell itself is focused
      // (e.g. after Escape returned focus here). Stop propagation so the
      // parent gridcell's grid-navigation handler doesn't ALSO treat Enter
      // as "activate row" and open the record.
      if (e.key === 'F2' || e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleStartEdit();
      }
      // Escape on a SELECTED cell (not editing) exits the selected state:
      // blurring closes the popped selection field. Pairs with the editor's
      // Escape (commit + close back to selected) for a two-step exit:
      // Escape leaves edit mode, Escape again leaves the selection.
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        cellRef.current?.blur();
      }
    },
    [isEditing, handleStartEdit]
  );

  // Cancel any pending badge navigation on unmount so it never fires after
  // the cell is gone (e.g. a virtualized row scrolled away).
  useEffect(() => () => cancelPendingBadgeNav(), [cancelPendingBadgeNav]);

  const handleKeyDownInput = useCallback(
    async (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        // A prior failed save leaves the editor open with the error shown —
        // Escape is then the abandon path: revert the draft and close
        // without retrying the failing save. Without this hatch an invalid
        // draft could never be escaped, since blur/outside-click commit too.
        if (errorMessage) {
          pendingCloseKindRef.current = 'cancel';
          cancelEdit();
          // Return focus to the outer cell element
          setTimeout(() => cellRef.current?.focus(), 0);
          return;
        }
        // Escape discards the draft and closes the editor without saving,
        // matching every other inline-edit surface. An unchanged draft has
        // nothing to discard, so it still closes via commit()'s no-op path
        // (short-circuits without calling onSave when the draft equals the
        // current value).
        if (valuesEqual(draftValue, effectiveValue)) {
          const saved = await commit();
          if (saved) {
            // Return focus to the outer cell so it stays selected.
            setTimeout(() => cellRef.current?.focus(), 0);
          }
          return;
        }
        pendingCloseKindRef.current = 'cancel';
        cancelEdit();
        setTimeout(() => cellRef.current?.focus(), 0);
        return;
      }

      if (e.key === 'Tab') {
        e.preventDefault();
        const saved = await commit();
        if (saved && onTabNext) {
          onTabNext();
        }
        return;
      }

      if (e.key === 'Enter') {
        // Let multi-line/multi-value/association editors keep their default
        // Enter behaviour (new line, add-value, listbox select) — the same
        // set useInlineEdit's own handleKeyDown skips via ownsEnterKey.
        if (descriptor.ownsEnterKey) {
          return;
        }
        // Let Enter pass through when a dropdown/popover is open (select, date picker, etc.).
        // These components need Enter to confirm selection.
        if (isPopoverOpenWithin(overlayRef.current)) {
          // Don't intercept — let the dropdown/datepicker handle Enter
          return;
        }
        e.preventDefault();
        const saved = await commit();
        if (saved && onEnterNext) {
          onEnterNext();
        }
      }
    },
    [
      errorMessage,
      draftValue,
      effectiveValue,
      cancelEdit,
      commit,
      onTabNext,
      onEnterNext,
      descriptor.ownsEnterKey,
    ]
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // The edit overlay anchors to the gridcell when there is one (production),
  // falling back to the cell div itself (standalone/unit-test renders). The
  // selection overlay requires a real gridcell — without one there is no
  // roving focus model to reflect.
  const editAnchorEl =
    gridcellEl === undefined ? null : (gridcellEl ?? cellRef.current);
  const showSelectionField =
    !!gridcellEl && isSelected && !isEditing && !disabled;
  // Long-form values (markdown, textarea) reveal in the selection field as
  // wrapping block content with a scroll cap — forcing them onto the
  // single nowrap line every other type uses would clip multi-paragraph
  // notes into one unreadable strip.
  const isLongTextValue =
    !!propertyType &&
    (propertyType.field_type_name === 'markdown' ||
      propertyType.field_type_name === 'textarea');

  return (
    <div
      ref={cellRef}
      className={cx(
        'relative w-full h-full flex items-center',
        !isEditing && !disabled && 'cursor-pointer',
        // Selecting (single click) focuses this div; the visible selection
        // state is the popped-field overlay, so suppress the UA outline.
        'focus:outline-none'
      )}
      // tabIndex={-1} unconditionally so the div is always programmatically
      // focusable (click-to-select, and focus return after Escape). The
      // parent gridcell manages roving tabindex.
      tabIndex={-1}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDownCell}
      data-entity-id={entityId}
      data-property-name={propertyName}
    >
      {/* Screen-reader live region for edit mode announcements */}
      <div aria-live='assertive' className='sr-only' aria-atomic='true'>
        {announcement}
      </div>

      {/* The in-grid value is always rendered (the overlay covers it while
          selected/editing) so row layout never shifts. */}
      <CellReadValue
        value={effectiveValue}
        propertyType={propertyType}
        association={association}
        truncate
        onBadgeClick={badgeClickHandler}
      />

      {/* Attio-style popped field: selection shows the full untruncated
          value; editing swaps the same container's content for the editor. */}
      {showSelectionField && gridcellEl && (
        <CellFieldOverlay
          anchorEl={gridcellEl}
          mode='selected'
          onDoubleClick={handleStartEdit}
        >
          {isLongTextValue ? (
            <div className='max-h-80 w-full overflow-y-auto whitespace-normal py-2 text-sm'>
              <CellReadValue
                value={effectiveValue}
                propertyType={propertyType}
                association={association}
                onBadgeClick={badgeClickHandler}
              />
            </div>
          ) : (
            <span className='whitespace-nowrap text-sm'>
              <CellReadValue
                value={effectiveValue}
                propertyType={propertyType}
                association={association}
                onBadgeClick={badgeClickHandler}
              />
            </span>
          )}
        </CellFieldOverlay>
      )}

      {isEditing && editAnchorEl && (
        <CellFieldOverlay
          anchorEl={editAnchorEl}
          mode='editing'
          layout={editOverlayLayout}
          cardMinWidthPx={editCardMinWidthPx}
          overlayRef={overlayRef}
          footer={
            errorMessage ? (
              <div
                role='alert'
                className={cx(
                  'mt-1 rounded-md px-2 py-1',
                  'bg-[var(--color-error-600)] text-white text-xs shadow-md',
                  'pointer-events-none'
                )}
              >
                {errorMessage}
              </div>
            ) : undefined
          }
        >
          <div
            className={getChromeOverrides(descriptor.kind, 'xs')}
            // Capture blur at the container level so focus moving between
            // sub-elements (e.g. dropdown options) doesn't trigger a
            // premature save
            onBlur={handleBlur}
            onKeyDown={handleKeyDownInput}
          >
            {association && (
              <AssociatedCrmObjectInput
                associationType={association.associationType}
                value={(draftValue as string[] | null | undefined) ?? []}
                onChange={handleDraftChange}
                orgId={association.orgId}
                sourceEntityId={entityId}
                size='xs'
                // Single associations: embedded search-list, no trigger and no
                // nested popover — the popped field IS the picker. Multi
                // associations keep the MultiSelect input, opened immediately.
                variant={isSingleAssociation ? 'embedded' : 'default'}
                defaultOpen={!isSingleAssociation}
                allowClear={association.allowClear ?? true}
                // The popped field is the chrome; the picker's standalone
                // trigger box would double it up (cx merge drops its
                // ring-1/shadow-xs/bg in favour of these).
                triggerClassName='ring-0 shadow-none bg-transparent'
              />
            )}
            {!association && propertyType && (
              <PropertyInput
                propertyType={propertyType}
                value={
                  draftValue as string | number | object | null | undefined
                }
                onChange={handleDraftChange}
                disabled={false}
                inputRef={inputRef}
                multiValueDisplay='card'
                markdownDisplay='card'
                phoneDisplay='card'
                textDisplay='card'
                // The cell's compact type/padding/icon scale, replacing the
                // `[&_input]:text-sm` style overrides that used to force it.
                size='xs'
                autoOpenPicker={isPickerEditor || isDateEditor}
              />
            )}
          </div>
        </CellFieldOverlay>
      )}
    </div>
  );
};

export const EditableCell = React.memo(EditableCellInner);
